/**
 * Persona Model
 * AI-powered audience generation and persona management
 *
 * This module provides types and transformations for working with AI-generated personas.
 * Personas represent target audiences with AI-generated descriptions, keywords, and segment recommendations.
 */
// ============================================================================
// Transformation Functions (Service -> SDK)
// ============================================================================
/**
 * Converts service curve point to SDK curve point
 */
export function serviceCurvePointToCurvePoint(serviceCurvePoint) {
    return {
        reach: serviceCurvePoint.reach,
        relevance: serviceCurvePoint.relevance
    };
}
/**
 * Converts service segment recommendation to SDK segment recommendation
 */
export function serviceSegmentRecommendationToSegmentRecommendation(serviceSegmentRecommendation) {
    return {
        description: serviceSegmentRecommendation.description,
        curvePoint: serviceCurvePointToCurvePoint(serviceSegmentRecommendation.curve_point)
    };
}
/**
 * Converts service AI segment to SDK AI segment
 */
export function serviceAISegmentToAISegment(serviceAISegment) {
    return {
        id: serviceAISegment.id,
        name: serviceAISegment.name,
        orgId: serviceAISegment.org_id,
        reachRelevance: serviceCurvePointToCurvePoint(serviceAISegment.reach_relevance),
        lastRefreshed: serviceAISegment.last_refreshed
            ? new Date(serviceAISegment.last_refreshed)
            : null,
        createdAt: new Date(serviceAISegment.created_at),
        status: serviceAISegment.status
    };
}
/**
 * Converts service publish setting to SDK publish setting
 */
export function servicePublishSettingToPublishSetting(servicePublishSetting) {
    return {
        name: servicePublishSetting.name,
        orgId: servicePublishSetting.org_id,
        curvePoint: serviceCurvePointToCurvePoint(servicePublishSetting.curve_point)
    };
}
/**
 * Converts service persona description to SDK persona description
 */
export function servicePersonaDescriptionToPersonaDescription(servicePersonaDescription) {
    return {
        name: servicePersonaDescription.name,
        summary: servicePersonaDescription.summary,
        details: servicePersonaDescription.details
    };
}
/**
 * Converts service persona to SDK persona
 */
export function servicePersonaToPersona(servicePersona) {
    return {
        id: servicePersona.id,
        description: servicePersonaDescriptionToPersonaDescription(servicePersona.description),
        keywords: servicePersona.keywords,
        userKeywords: servicePersona.user_keywords,
        segmentRecommendations: servicePersona.segment_recommendations?.map((rec) => serviceSegmentRecommendationToSegmentRecommendation(rec)),
        reachCurve: servicePersona.reach_curve?.map((point) => serviceCurvePointToCurvePoint(point)),
        segments: servicePersona.segments?.map((seg) => serviceAISegmentToAISegment(seg)),
        publishSettings: servicePersona.publish_settings?.map((setting) => servicePublishSettingToPublishSetting(setting)),
        audienceDescriptionType: servicePersona.audience_description_type,
        notes: servicePersona.notes,
        createdBy: servicePersona.created_by,
        updatedBy: servicePersona.updated_by,
        createdAt: servicePersona.created_at ? new Date(servicePersona.created_at) : undefined,
        updatedAt: servicePersona.updated_at ? new Date(servicePersona.updated_at) : undefined,
        status: servicePersona.status
    };
}
/**
 * Converts service generate persona request to SDK generate persona request
 */
export function serviceGeneratePersonaRequestToGeneratePersonaRequest(serviceRequest) {
    return {
        audienceDescription: serviceRequest.audience_description,
        audienceDescriptionType: serviceRequest.audience_description_type
    };
}
/**
 * Converts service upsert persona request to SDK upsert persona request
 */
export function serviceUpsertPersonaRequestToUpsertPersonaRequest(serviceRequest) {
    return {
        ...servicePersonaToPersona(serviceRequest),
        publishSegments: serviceRequest.publish_segments
    };
}
// ============================================================================
// Transformation Functions (SDK -> Service)
// ============================================================================
/**
 * Converts SDK curve point to service curve point
 */
export function curvePointToServiceCurvePoint(curvePoint) {
    return {
        reach: curvePoint.reach,
        relevance: curvePoint.relevance
    };
}
/**
 * Converts SDK segment recommendation to service segment recommendation
 */
export function segmentRecommendationToServiceSegmentRecommendation(segmentRecommendation) {
    return {
        description: segmentRecommendation.description,
        curve_point: curvePointToServiceCurvePoint(segmentRecommendation.curvePoint)
    };
}
/**
 * Converts SDK AI segment to service AI segment
 */
export function aiSegmentToServiceAISegment(aiSegment) {
    return {
        id: aiSegment.id,
        name: aiSegment.name,
        org_id: aiSegment.orgId,
        reach_relevance: curvePointToServiceCurvePoint(aiSegment.reachRelevance),
        last_refreshed: aiSegment.lastRefreshed?.toISOString() ?? null,
        created_at: aiSegment.createdAt.toISOString(),
        status: aiSegment.status
    };
}
/**
 * Converts SDK publish setting to service publish setting
 */
export function publishSettingToServicePublishSetting(publishSetting) {
    return {
        name: publishSetting.name,
        org_id: publishSetting.orgId,
        curve_point: curvePointToServiceCurvePoint(publishSetting.curvePoint)
    };
}
/**
 * Converts SDK persona description to service persona description
 */
export function personaDescriptionToServicePersonaDescription(personaDescription) {
    return {
        name: personaDescription.name,
        summary: personaDescription.summary,
        details: personaDescription.details
    };
}
/**
 * Converts SDK persona to service persona
 */
export function personaToServicePersona(persona) {
    return {
        id: persona.id,
        description: personaDescriptionToServicePersonaDescription(persona.description),
        keywords: persona.keywords,
        user_keywords: persona.userKeywords,
        segment_recommendations: persona.segmentRecommendations?.map((rec) => segmentRecommendationToServiceSegmentRecommendation(rec)),
        reach_curve: persona.reachCurve?.map((point) => curvePointToServiceCurvePoint(point)),
        segments: persona.segments?.map((seg) => aiSegmentToServiceAISegment(seg)),
        publish_settings: persona.publishSettings?.map((setting) => publishSettingToServicePublishSetting(setting)),
        audience_description_type: persona.audienceDescriptionType,
        notes: persona.notes,
        created_by: persona.createdBy,
        updated_by: persona.updatedBy,
        created_at: persona.createdAt?.toISOString(),
        updated_at: persona.updatedAt?.toISOString(),
        status: persona.status
    };
}
/**
 * Converts SDK generate persona request to service generate persona request
 */
export function generatePersonaRequestToServiceGeneratePersonaRequest(request) {
    return {
        audience_description: request.audienceDescription,
        audience_description_type: request.audienceDescriptionType
    };
}
/**
 * Converts SDK upsert persona request to service upsert persona request
 */
export function upsertPersonaRequestToServiceUpsertPersonaRequest(request) {
    return {
        ...personaToServicePersona(request),
        publish_segments: request.publishSegments
    };
}
//# sourceMappingURL=persona.js.map